import type { FC } from "react";
import type { ScoringType, CriterionKey, FitCriterionKey } from "./types";
import {
  RevenueEditor,
  initialBand as revenueBand,
  hasErrors as revenueHasErrors,
  getBandSummary as revenueSummary,
} from "./components/criteria/RevenueEditor";
import {
  EmployeesEditor,
  initialBand as employeesBand,
  hasErrors as employeesHasErrors,
  getBandSummary as employeesSummary,
} from "./components/criteria/EmployeesEditor";
import {
  GeographyEditor,
  initialBand as geographyBand,
  hasErrors as geographyHasErrors,
  getBandSummary as geographySummary,
} from "./components/criteria/GeographyEditor";
import {
  IndustryEditor,
  initialBand as industryBand,
  hasErrors as industryHasErrors,
  getBandSummary as industrySummary,
} from "./components/criteria/IndustryEditor";
import {
  GlobalPresenceEditor,
  initialBand as globalPresenceBand,
  hasErrors as globalPresenceHasErrors,
  getBandSummary as globalPresenceSummary,
  sanitize as globalPresenceSanitize,
} from "./components/criteria/GlobalPresenceEditor";
import {
  ProductIntensityEditor,
  initialBand as productIntensityBand,
  hasErrors as productIntensityHasErrors,
  getBandSummary as productIntensitySummary,
} from "./components/criteria/ProductIntensityEditor";
import {
  ProductInstallAgeEditor,
  initialBand as productInstallAgeBand,
  hasErrors as productInstallAgeHasErrors,
  getBandSummary as productInstallAgeSummary,
} from "./components/criteria/ProductInstallAgeEditor";
import {
  ProductPresenceEditor,
  initialBand as productPresenceBand,
  hasErrors as productPresenceHasErrors,
  getBandSummary as productPresenceSummary,
} from "./components/criteria/ProductPresenceEditor";
import {
  SignalStrengthEditor,
  initialBand as signalStrengthBand,
  hasErrors as signalStrengthHasErrors,
  getBandSummary as signalStrengthSummary,
} from "./components/criteria/SignalStrengthEditor";
import {
  ContextTypeEditor,
  initialBand as contextTypeBand,
  hasErrors as contextTypeHasErrors,
  getBandSummary as contextTypeSummary,
} from "./components/criteria/ContextTypeEditor";
import {
  BuyersJourneyEditor,
  initialBand as buyersJourneyBand,
  hasErrors as buyersJourneyHasErrors,
  getBandSummary as buyersJourneySummary,
} from "./components/criteria/BuyersJourneyEditor";
import {
  SignalLocationEditor,
  initialBand as signalLocationBand,
  hasErrors as signalLocationHasErrors,
  getBandSummary as signalLocationSummary,
} from "./components/criteria/SignalLocationEditor";
import {
  CategoryPenetrationEditor,
  initialBand as categoryPenetrationBand,
  hasErrors as categoryPenetrationHasErrors,
  getBandSummary as categoryPenetrationSummary,
} from "./components/criteria/CategoryPenetrationEditor";
import {
  VendorPenetrationEditor,
  initialBand as vendorPenetrationBand,
  hasErrors as vendorPenetrationHasErrors,
  getBandSummary as vendorPenetrationSummary,
} from "./components/criteria/VendorPenetrationEditor";
import {
  CloudMaturityEditor,
  initialBand as cloudMaturityBand,
  hasErrors as cloudMaturityHasErrors,
  getBandSummary as cloudMaturitySummary,
  sanitize as cloudMaturitySanitize,
} from "./components/criteria/CloudMaturityEditor";
import {
  ITSpendEditor,
  initialBand as itSpendBand,
  hasErrors as itSpendHasErrors,
  getBandSummary as itSpendSummary,
} from "./components/criteria/ITSpendEditor";

type EditorProps = {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
};

const CRITERIA_TOOLTIP =
  "Multiple values can match, with the goal of prioritizing the highest value among those matched. The maximum point value of the matched criteria is divided by the maximum point value and then multiplied by the weight assigned to the overall criteria to calculate the final contribution to the total score.";

export interface CriterionRegistryEntry {
  editor: FC<EditorProps>;
  initialBand: unknown;
  hasErrors: (bands: unknown[]) => boolean;
  getBandSummary: (band: unknown) => string;
  label: string;
  tooltip?: string;
  pickerTooltip: string;
  isFit: boolean;
  /** Whether this criterion relates to tech installs (affects Install Localization visibility). */
  isTechInstall?: boolean;
  /** Optional: sanitize the criterion before sending to the backend. */
  sanitize?: (criterion: ScoringType) => ScoringType;
}

export const CRITERION_REGISTRY: Partial<
  Record<CriterionKey, CriterionRegistryEntry>
> = {
  revenue: {
    editor: RevenueEditor,
    initialBand: revenueBand,
    hasErrors: revenueHasErrors,
    getBandSummary: revenueSummary,
    label: "Revenue",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip: "Prioritize companies based on revenue.",
    isFit: true,
  },
  employees: {
    editor: EmployeesEditor,
    initialBand: employeesBand,
    hasErrors: employeesHasErrors,
    getBandSummary: employeesSummary,
    label: "Employees",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip: "Prioritize companies based on their employee count.",
    isFit: true,
  },
  geography: {
    editor: GeographyEditor,
    initialBand: geographyBand,
    hasErrors: geographyHasErrors,
    getBandSummary: geographySummary,
    label: "Geography",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip: "Prioritize companies based on geographic location.",
    isFit: true,
  },
  industry: {
    editor: IndustryEditor,
    initialBand: industryBand,
    hasErrors: industryHasErrors,
    getBandSummary: industrySummary,
    label: "Industry",
    pickerTooltip: "Prioritize companies based on their industry.",
    isFit: true,
  },
  globalPresence: {
    editor: GlobalPresenceEditor,
    initialBand: globalPresenceBand,
    hasErrors: globalPresenceHasErrors,
    getBandSummary: globalPresenceSummary,
    sanitize: globalPresenceSanitize,
    label: "Global Presence",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip: "Prioritize companies based on their global presence.",
    isFit: true,
  },
  // ── Tech Install criteria ────────────────────────────────────────────────
  productIntensity: {
    editor: ProductIntensityEditor,
    initialBand: productIntensityBand,
    hasErrors: productIntensityHasErrors,
    getBandSummary: productIntensitySummary,
    label: "Product Intensity",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on the intensity of product installations.",
    isFit: true,
    isTechInstall: true,
  },
  productInstallAgeMonths: {
    editor: ProductInstallAgeEditor,
    initialBand: productInstallAgeBand,
    hasErrors: productInstallAgeHasErrors,
    getBandSummary: productInstallAgeSummary,
    label: "Product Install Age",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on how long products have been installed.",
    isFit: true,
    isTechInstall: true,
  },
  productCategoryPenetration: {
    editor: CategoryPenetrationEditor,
    initialBand: categoryPenetrationBand,
    hasErrors: categoryPenetrationHasErrors,
    getBandSummary: categoryPenetrationSummary,
    label: "Products in a Category",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on the number of installs in a product category.",
    isFit: true,
    isTechInstall: true,
  },
  vendorPenetration: {
    editor: VendorPenetrationEditor,
    initialBand: vendorPenetrationBand,
    hasErrors: vendorPenetrationHasErrors,
    getBandSummary: vendorPenetrationSummary,
    label: "Vendor Penetration",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on the number of vendor installs.",
    isFit: true,
    isTechInstall: true,
  },
  products: {
    editor: ProductPresenceEditor,
    initialBand: productPresenceBand,
    hasErrors: productPresenceHasErrors,
    getBandSummary: productPresenceSummary,
    label: "Product Presence",
    tooltip:
      "Multiple criteria can match, and each contributes to the total score based on its weight.",
    pickerTooltip:
      "Prioritize companies based on whether specific products are installed.",
    isFit: true,
    isTechInstall: true,
  },
  cloudMaturity: {
    editor: CloudMaturityEditor,
    initialBand: cloudMaturityBand,
    hasErrors: cloudMaturityHasErrors,
    getBandSummary: cloudMaturitySummary,
    sanitize: cloudMaturitySanitize,
    label: "Cloud Maturity",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on the maturity of their cloud tech stack.",
    isFit: true,
    isTechInstall: true,
  },
  spend: {
    editor: ITSpendEditor,
    initialBand: itSpendBand,
    hasErrors: itSpendHasErrors,
    getBandSummary: itSpendSummary,
    label: "IT Spend",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip: "Prioritize companies based on their IT spending.",
    isFit: true,
  },
  // ── Intent criteria ──────────────────────────────────────────────────────
  signalStrength: {
    editor: SignalStrengthEditor,
    initialBand: signalStrengthBand,
    hasErrors: signalStrengthHasErrors,
    getBandSummary: signalStrengthSummary,
    label: "Signal Strength",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on the strength of their intent signals.",
    isFit: false,
  },
  contextType: {
    editor: ContextTypeEditor,
    initialBand: contextTypeBand,
    hasErrors: contextTypeHasErrors,
    getBandSummary: contextTypeSummary,
    label: "Context Type",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on the buying context type of their intent signals.",
    isFit: false,
  },
  buyersJourney: {
    editor: BuyersJourneyEditor,
    initialBand: buyersJourneyBand,
    hasErrors: buyersJourneyHasErrors,
    getBandSummary: buyersJourneySummary,
    label: "Buyer's Journey",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on where they are in the buyer's journey.",
    isFit: false,
  },
  location: {
    editor: SignalLocationEditor,
    initialBand: signalLocationBand,
    hasErrors: signalLocationHasErrors,
    getBandSummary: signalLocationSummary,
    label: "Signal Location",
    tooltip: CRITERIA_TOOLTIP,
    pickerTooltip:
      "Prioritize companies based on the location of their intent signals.",
    isFit: false,
  },
};

// ── Derived exports — consumers import these instead of types.ts constants ──

export const FIT_CRITERION_KEYS = (
  Object.entries(CRITERION_REGISTRY) as [CriterionKey, CriterionRegistryEntry][]
)
  .filter(([, e]) => e.isFit)
  .map(([k]) => k) as FitCriterionKey[];

export const INTENT_CRITERION_KEYS: CriterionKey[] = (
  Object.entries(CRITERION_REGISTRY) as [CriterionKey, CriterionRegistryEntry][]
)
  .filter(([, e]) => !e.isFit)
  .map(([k]) => k);

export const CRITERION_LABELS = Object.fromEntries(
  (
    Object.entries(CRITERION_REGISTRY) as [
      CriterionKey,
      CriterionRegistryEntry,
    ][]
  ).map(([k, e]) => [k, e.label])
) as Record<CriterionKey, string>;

export const CRITERION_TOOLTIPS = Object.fromEntries(
  (
    Object.entries(CRITERION_REGISTRY) as [
      CriterionKey,
      CriterionRegistryEntry,
    ][]
  ).map(([k, e]) => [k, e.tooltip])
) as Record<CriterionKey, string>;

export function getCriterionKey(c: ScoringType): CriterionKey | null {
  return (
    (Object.keys(CRITERION_REGISTRY) as CriterionKey[]).find(
      (k) => c[k] !== undefined
    ) ?? null
  );
}
